import { useActivateCard, useWealthOverview } from '@egw/api'
import { inAppChatService } from '@egw/app-chat'
import { Button, ButtonVariant, Input } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { fromCardExpirationDateToBEDate } from '@egw/utils'
import { CheckCircleIcon } from '@heroicons/react/16/solid'
import { type FC, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { sendEvent } from '@egw/events'
import CardFront from '../card-front/card-front'
import { SetPin } from '../set-pin-attribute/set-pin'

const lastDigitsFieldId = 'lastDigits'
const expirationDateFieldId = 'expirationDate'

interface FormData {
  lastDigits: string
  expirationDate: string
}

interface ActivateCardProps {
  cardId: string
  setModalTitle: (_: string) => void
  closeModal: () => void
  alias: string
}

const ActivateCard: FC<ActivateCardProps> = ({ cardId, setModalTitle, closeModal, alias }) => {
  const [isActivationError, setIsActivationError] = useState(false)
  const [isActivated, setIsActivated] = useState(false)
  const [showSetPin, setShowSetPin] = useState(false)

  const { t } = useTranslation('features-account')

  const { refetch } = useWealthOverview({ enabled: false })

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isDirty, isValid },
    setError,
  } = useForm({
    mode: 'onTouched',
    defaultValues: {
      lastDigits: '',
      expirationDate: '',
    },
  })

  const { isLoading, mutate: activateCard } = useActivateCard({
    onSuccess: (data) => {
      if (data.__typename === 'CardStatusUpdateError') {
        setIsActivationError(true)
        setModalTitle(t('debit_card.activate.unable_to_activate_card'))
        sendEvent('eg_cardActivate_fail', {
          eventSource: 'egWealth',
          category: 'card-activation',
          subCategory: 'result',
        })
        return
      }
      sendEvent('eg_cardActivate_success', {
        eventSource: 'egWealth',
        category: 'card-activation',
        subCategory: 'result',
      })
      setModalTitle(t('debit_card.activate.card_activated'))
      setIsActivated(true)
      void refetch()
    },
    onError: () => {
      sendEvent('eg_cardActivate_fail', {
        eventSource: 'egWealth',
        category: 'card-activation',
        subCategory: 'result',
      })
      setIsActivationError(true)
      setModalTitle(t('debit_card.activate.unable_to_activate_card'))
    },
  })

  const onSubmit = handleSubmit((formData: unknown): void => {
    const data = formData as FormData
    const expirationDate = fromCardExpirationDateToBEDate(data.expirationDate)

    if (!expirationDate) {
      setError(expirationDateFieldId, {
        type: 'manual',
        message: t('debit_card.activate.ensure_you_entered_the_correct_expiration_date'),
      })
      return
    }

    void activateCard({
      cardId,
      last4PAN: data.lastDigits,
      expirationDate: expirationDate || '',
    })
  })

  const contactSupport = (): void => {
    inAppChatService.open()
    closeModal()
  }

  const updateModalTitle = (title: string): void => {
    setModalTitle(title)
  }

  const tryAgain = (): void => {
    setIsActivationError(false)
    updateModalTitle(t('card.activate.title'))
  }

  return (
    <>
      {!isActivationError && (
        <>
          {!isActivated && (
            <form>
              <Controller
                name={lastDigitsFieldId}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={lastDigitsFieldId}
                    placeholder={t('debit_card.activate.last_4_digits_of_card_number')}
                    valueFormatter={(value: string) => {
                      const digitsOnly = value.replace(/\D/g, '')
                      return digitsOnly.slice(0, 4)
                    }}
                    inputMode="numeric"
                  />
                )}
              />

              <Controller
                name={expirationDateFieldId}
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id={expirationDateFieldId}
                    valueFormatter={(value: string) => {
                      const digitsOnly = value.replace(/\D/g, '')
                      if (digitsOnly.length <= 2) {
                        return digitsOnly
                      }
                      return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}` // MM/YY
                    }}
                    placeholder={t('debit_card.activate.card_expiration_date')}
                    inputMode="numeric"
                    className="mt-4"
                  />
                )}
              />
              {formErrors.expirationDate ? <p>{formErrors.expirationDate.message}</p> : null}

              <Button
                id="activateCardSubmit"
                variant={ButtonVariant.Primary}
                loadingContent={t('debit_card.activate.activating')}
                className="mb-4 w-full mt-4"
                isDisabled={!isDirty || !isValid}
                onClick={onSubmit}
                isLoading={isLoading}
              >
                {t('debit_card.activate.button_title')}
              </Button>
            </form>
          )}

          {isActivated ? (
            <>
              {!showSetPin && (
                <>
                  <div className="relative mb-6 flex justify-center">
                    <CardFront height={100} />
                    <div className="absolute -bottom-4 bg-white rounded-full">
                      <CheckCircleIcon className="w-10 h-10 fill-green-500" />
                    </div>
                  </div>
                  <Button
                    variant={ButtonVariant.Primary}
                    id="setPin"
                    className="mt-4"
                    onClick={() => {
                      setShowSetPin(true)
                    }}
                  >
                    {t('debit_card.set_pin_title')}
                  </Button>
                </>
              )}

              {showSetPin ? <SetPin isOpen={showSetPin} alias={alias} closeModal={closeModal} cardId={cardId} /> : null}
            </>
          ) : null}
        </>
      )}

      {isActivationError ? (
        <>
          <p className="mb-2 text-zinc-700">{t('debit_card.activate.there_was_a_problem_message')}</p>
          <Button id="activateCardTryAgain" className="mt-4" onClick={tryAgain}>
            {t('debit_card.activate.try_again')}
          </Button>
          <Button id="clientSupport" className="mt-4" onClick={contactSupport}>
            {t('debit_card.activate.contact_client_support')}
          </Button>
        </>
      ) : null}
    </>
  )
}

export default ActivateCard
